import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Image, Lightbulb, Inbox, Send, Settings, Users, LogOut, DollarSign, BookOpen, Menu, X } from 'lucide-react';
import { ClientSelector } from './ClientSelector';
import { NotificationBell } from './NotificationBell';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';

export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isClientView = location.pathname.startsWith('/client');
  const { activeClient, resolvedLogoUrl } = useClient();
  const { logout } = useAuth();
  const [newLeadCount, setNewLeadCount] = useState(0);
  const [portalFeedbackCount, setPortalFeedbackCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (isClientView) return;
    const fetchLeadCount = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(data => setNewLeadCount(Array.isArray(data) ? data.filter(l => l.status === 'new').length : 0))
        .catch(() => {});
    };
    fetchLeadCount();
    const interval = setInterval(fetchLeadCount, 60000);
    return () => clearInterval(interval);
  }, [isClientView]);

  useEffect(() => {
    if (isClientView) return;
    const fetchFeedback = () => {
      const clientSlug = localStorage.getItem('activeClientSlug') || '';
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agency/inbox/count`, {
        credentials: 'include',
        headers: { 'X-Client-Id': clientSlug }
      })
        .then(r => r.ok ? r.json() : {})
        .then(data => setPortalFeedbackCount(data.total || 0))
        .catch(() => {});
    };
    fetchFeedback();
    const interval = setInterval(fetchFeedback, 30000);
    window.addEventListener('inbox-updated', fetchFeedback);
    return () => { clearInterval(interval); window.removeEventListener('inbox-updated', fetchFeedback); };
  }, [isClientView, activeClient]);

  const logo = resolvedLogoUrl || null;
  const label = isClientView ? 'Client Portal' : 'Social Planner';
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Desktop inline nav link
  const navLink = (to, label, Icon, testId) => (
    <Link
      to={to}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
        isActive(to) ? 'text-white shadow-lg' : 'text-[#475569] hover:bg-[#f1f5f9]'
      }`}
      style={isActive(to) ? { backgroundColor: 'var(--brand-primary)', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' } : {}}
      data-testid={testId}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
    </Link>
  );

  // Mobile drawer link
  const drawerLink = (to, label, Icon, testId, badge) => (
    <Link
      to={to}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
        isActive(to) ? 'text-white' : 'text-[#334155] hover:bg-[#f1f5f9]'
      }`}
      style={isActive(to) ? { backgroundColor: 'var(--brand-primary)' } : {}}
      data-testid={`${testId}-mobile`}
    >
      <Icon className="w-5 h-5" />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span className="min-w-[20px] h-[20px] flex items-center justify-center bg-rose-500 text-white text-[10px] font-bold rounded-full px-1">
          {badge}
        </span>
      )}
    </Link>
  );

  const agencyLinks = [
    { to: '/dashboard', label: 'Calendar', icon: Calendar, testId: 'nav-dashboard', badge: 0 },
    { to: '/agency', label: 'Needs Action', icon: Inbox, testId: 'nav-agency', badge: portalFeedbackCount },
    { to: '/publish', label: 'Publish', icon: Send, testId: 'nav-publish', badge: 0 },
    { to: '/library', label: 'Library', icon: Image, testId: 'nav-library', badge: 0 },
    { to: '/settings', label: 'Settings', icon: Settings, testId: 'nav-settings', badge: 0 },
    { to: '/leads', label: 'Leads', icon: Users, testId: 'nav-leads', badge: newLeadCount },
  ];

  const marketingLinks = [
    { to: '/pricing', label: 'Pricing', icon: DollarSign, testId: 'nav-pricing' },
    { to: '/how-it-works', label: 'How It Works', icon: BookOpen, testId: 'nav-how-it-works' },
  ];

  const clientLinks = [
    { to: '/client', label: 'Calendar', icon: Calendar, testId: 'nav-client-calendar', badge: 0 },
    { to: '/client/ideas', label: 'Submit Idea', icon: Lightbulb, testId: 'nav-client-ideas', badge: 0 },
  ];

  const totalBadges = newLeadCount + portalFeedbackCount;

  return (
    <>
      <header className="glass-header sticky top-0 z-50" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            {/* Logo + Client Selector */}
            <div className="flex items-center gap-3 min-w-0 shrink-0 max-w-[280px] lg:max-w-none">
              <Link to={isClientView ? "/client" : "/dashboard"} className="flex items-center gap-2.5" data-testid="nav-logo">
                {logo ? (
                  <img src={logo} alt="" className="h-10 w-auto max-w-[80px] object-contain" />
                ) : (
                  <span className="text-sm font-bold text-[#003C59]">Post Planner</span>
                )}
                <div className="hidden sm:block">
                  <div className="text-[9px] uppercase tracking-[0.15em] text-[#64748b] font-medium">
                    {label}
                  </div>
                </div>
              </Link>
              {!isClientView && <ClientSelector />}
            </div>

            {/* Desktop Navigation (hidden on mobile) */}
            <nav className="hidden md:flex items-center gap-1">
              {isClientView ? (
                <>
                  <Link
                    to="/client"
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      isActive('/client') && !isActive('/client/ideas') ? 'text-white shadow-lg' : 'text-[#475569] hover:bg-[#f1f5f9]'
                    }`}
                    style={(isActive('/client') && !isActive('/client/ideas')) ? { backgroundColor: 'var(--brand-primary)', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' } : {}}
                    data-testid="nav-client-calendar"
                  >
                    <Calendar className="w-4 h-4" />
                    <span className="hidden sm:inline">Calendar</span>
                  </Link>
                  {navLink('/client/ideas', 'Submit Idea', Lightbulb, 'nav-client-ideas')}
                </>
              ) : (
                <>
                  <Link
                    to="/dashboard"
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      isActive('/dashboard') || (isActive('/month') && !isActive('/client')) ? 'text-white shadow-lg' : 'text-[#475569] hover:bg-[#f1f5f9]'
                    }`}
                    style={(isActive('/dashboard') || (isActive('/month') && !isActive('/client'))) ? { backgroundColor: 'var(--brand-primary)', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' } : {}}
                    data-testid="nav-dashboard"
                  >
                    <Calendar className="w-4 h-4" />
                    <span>Calendar</span>
                  </Link>
                  <Link
                    to="/agency"
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      isActive('/agency') ? 'text-white shadow-lg' : 'text-[#475569] hover:bg-[#f1f5f9]'
                    }`}
                    style={isActive('/agency') ? { backgroundColor: 'var(--brand-primary)', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' } : {}}
                    data-testid="nav-agency"
                  >
                    <Inbox className="w-4 h-4" />
                    <span>Needs Action</span>
                    {portalFeedbackCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-rose-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse" data-testid="agency-feedback-badge">
                        {portalFeedbackCount}
                      </span>
                    )}
                  </Link>
                  {navLink('/publish', 'Publish', Send, 'nav-publish')}
                  {navLink('/library', 'Library', Image, 'nav-library')}
                  {navLink('/settings', 'Settings', Settings, 'nav-settings')}
                  <Link
                    to="/leads"
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      isActive('/leads') ? 'text-white shadow-lg' : 'text-[#475569] hover:bg-[#f1f5f9]'
                    }`}
                    style={isActive('/leads') ? { backgroundColor: 'var(--brand-primary)', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' } : {}}
                    data-testid="nav-leads"
                  >
                    <Users className="w-4 h-4" />
                    <span>Leads</span>
                    {newLeadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-amber-500 text-white text-[10px] font-bold rounded-full px-1" data-testid="lead-count-badge">
                        {newLeadCount}
                      </span>
                    )}
                  </Link>
                  <div className="hidden lg:contents">
                    <div className="h-5 w-px bg-[#e2e8f0] mx-1"></div>
                    {navLink('/pricing', 'Pricing', DollarSign, 'nav-pricing')}
                    {navLink('/how-it-works', 'How It Works', BookOpen, 'nav-how-it-works')}
                  </div>
                </>
              )}
              <div className="h-6 w-px bg-[#e2e8f0] mx-2"></div>
              {!isClientView && <NotificationBell />}
              {!isClientView && (
                <button
                  onClick={async () => { await logout(); navigate('/login'); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-[#475569] hover:bg-red-50 hover:text-red-600 transition-all ml-1"
                  data-testid="nav-logout"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden lg:inline">Sign Out</span>
                </button>
              )}
            </nav>

            {/* Mobile: notification bell + hamburger */}
            <div className="flex md:hidden items-center gap-1">
              {!isClientView && <NotificationBell />}
              <button
                onClick={() => setDrawerOpen(true)}
                className="relative flex items-center justify-center w-10 h-10 rounded-xl text-[#475569] hover:bg-[#f1f5f9] transition-colors"
                data-testid="mobile-menu-btn"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
                {totalBadges > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="mobile-drawer">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Drawer panel */}
          <div className="absolute top-0 right-0 bottom-0 w-[280px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                {logo ? (
                  <img src={logo} alt="" className="h-8 w-auto" />
                ) : (
                  <span className="text-sm font-bold text-[#003C59]">Post Planner</span>
                )}
                <span className="text-xs font-medium text-[#64748b] uppercase tracking-wider">{label}</span>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-[#475569] hover:bg-[#f1f5f9] transition-colors"
                data-testid="mobile-menu-close"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer links */}
            <div className="flex-1 overflow-y-auto py-3 px-3">
              {isClientView ? (
                <div className="space-y-1">
                  {clientLinks.map(link => drawerLink(link.to, link.label, link.icon, link.testId, link.badge))}
                </div>
              ) : (
                <>
                  <div className="space-y-1">
                    {agencyLinks.map(link => drawerLink(link.to, link.label, link.icon, link.testId, link.badge))}
                  </div>
                  <div className="h-px bg-gray-100 my-3 mx-2" />
                  <p className="px-4 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Pages</p>
                  <div className="space-y-1">
                    {marketingLinks.map(link => drawerLink(link.to, link.label, link.icon, link.testId, 0))}
                  </div>
                </>
              )}
            </div>

            {/* Drawer footer: Sign Out */}
            {!isClientView && (
              <div className="border-t border-gray-100 p-3">
                <button
                  onClick={async () => { setDrawerOpen(false); await logout(); navigate('/login'); }}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                  data-testid="nav-logout-mobile"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
